import { CaretDown, CaretUp } from "@phosphor-icons/react"
import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react"
import { type Placement } from "react-aria"
import { Popover } from "react-aria-components"

import { Icon } from "../icon"
import { Drawer } from "../overlays/modal/Drawer"
import {
  SearchableList,
  type SelectionProps,
} from "./searchableList/SearchableList"
import SearchInput from "./searchableList/SearchInput"
import useIsMobile from "./useIsMobile"

type SearchState = {
  search?: string
  setSearch?: (search: string) => void
}

const OpenContext = createContext<boolean | undefined>(undefined)

export const useIsComboboxOpen = () => {
  const context = useContext(OpenContext)
  if (context === undefined) {
    throw new Error("useIsOpen must be used within a Combobox")
  }
  return context
}

const DropdownIcon = ({
  openIcon = CaretUp,
  closedIcon = CaretDown,
}: {
  openIcon?: React.ElementType
  closedIcon?: React.ElementType
}) => {
  const isOpen = useIsComboboxOpen()

  return <Icon as={isOpen ? openIcon : closedIcon} />
}

export type ComboboxProps = {
  children: React.ReactNode
  drawerHeaderTitle: string
  closeOnSelect?: boolean
  placement?: Placement
  virtualized?: boolean
  shouldFilter?: boolean
  searchPlaceholder?: string
  searchRef?: React.RefObject<HTMLInputElement>
  isOpen?: boolean
  setIsOpen?: (isOpen: boolean) => void
  onOpenChange?: () => void
  onClickItem?: (id: string) => void
} & SelectionProps &
  SearchState

export const Combobox = ({
  children,
  trigger,
  triggerRef: incomingTriggerRef,
  drawerHeaderTitle,
  // Options
  closeOnSelect = true,
  virtualized = false,
  shouldFilter = true,
  placement = "bottom start",
  // Controlled search state (optional)
  search: controlledSearch,
  setSearch: controlledSetSearch,
  searchPlaceholder,
  searchRef: incomingSearchRef,
  // // Controlled open state (optional)
  isOpen: controlledIsOpen,
  setIsOpen: controlledSetIsOpen,
  onOpenChange,
  // Controlled selection state (optional)
  selectionMode,
  selectedId,
  onChangeSelectedId,
  selectedIds,
  onChangeSelectedIds,
  onClickItem,
}: ComboboxProps & {
  trigger?: React.ReactNode
  triggerRef?: React.RefObject<HTMLButtonElement>
}) => {
  if (!selectionMode && (selectedIds !== undefined || onChangeSelectedIds)) {
    // eslint-disable-next-line no-console
    console.warn(
      "Selection mode must be set to 'single' or 'multiple' to use selectedIds or onChangeSelectedIds"
    )
  }

  if (!incomingTriggerRef && !trigger) {
    // eslint-disable-next-line no-console
    console.warn("Combobox must have a trigger or triggerRef")
  }

  const isMobile = useIsMobile()
  const internalTriggerRef = useRef<HTMLButtonElement>(null)
  const triggerRef = incomingTriggerRef || internalTriggerRef
  const [internalSearch, setInternalSearch] = useState("")
  const search = controlledSearch || internalSearch
  const setSearch = controlledSetSearch || setInternalSearch
  const [internalIsOpen, setInternalIsOpen] = useState(false)
  const isOpen = controlledIsOpen || internalIsOpen
  const setIsOpen = controlledSetIsOpen || setInternalIsOpen

  const handleOnOpenChange = useCallback(
    (newOpenState: boolean) => {
      setSearch("")
      setIsOpen(newOpenState)
      onOpenChange?.()
    },

    [onOpenChange, setIsOpen, setSearch]
  )

  // Passes through onChangeSelectedIds to SearchableList but gives us a chance
  // to close the combobox and focus the trigger
  const handleMultipleSelectionChange = useCallback(
    (newSelection: string[] | null) => {
      if (closeOnSelect) {
        setSearch("")
        setIsOpen(false)
        if (triggerRef?.current) {
          triggerRef.current?.focus()
        }
      }
      onChangeSelectedIds?.(newSelection)
    },
    [closeOnSelect, setSearch, setIsOpen, triggerRef, onChangeSelectedIds]
  )

  const handleSingleSelectionChange = useCallback(
    (newSelection: string | null) => {
      if (closeOnSelect) {
        setSearch("")
        setIsOpen(false)
        if (triggerRef?.current) {
          triggerRef.current?.focus()
        }
      }
      onChangeSelectedId?.(newSelection)
    },
    [closeOnSelect, triggerRef, setSearch, setIsOpen, onChangeSelectedId]
  )

  const handleClickItem = useCallback(
    (id: string) => {
      if (closeOnSelect) {
        setSearch("")
        setIsOpen(false)
        if (triggerRef?.current) {
          triggerRef.current?.focus()
        }
      }
      onClickItem?.(id)
    },
    [closeOnSelect, triggerRef, setSearch, setIsOpen, onClickItem]
  )

  const triggerElement = useMemo(() => {
    if (trigger && isValidElement(trigger)) {
      return cloneElement(trigger, {
        // @ts-expect-error - ref is an unknown prop when cloning
        ref: triggerRef,
        onPress: () => {
          setIsOpen(true)
        },
      })
    }

    return null
  }, [trigger, triggerRef, setIsOpen])

  const ListComponent = useMemo(
    () => (virtualized ? SearchableList.VirtualizedList : SearchableList.List),
    [virtualized]
  )

  if (isMobile) {
    return (
      <OpenContext.Provider value={isOpen}>
        {triggerElement}
        <Drawer isOpen={isOpen} onOpenChange={handleOnOpenChange}>
          <Drawer.Header
            title={drawerHeaderTitle}
            className="p-3 border-b border-solid border-border-default"
          />
          {/* @ts-ignore - TODO: fix this */}
          <SearchableList
            search={search}
            onChangeSearch={setSearch}
            selectionMode={selectionMode}
            selectedId={selectedId}
            onChangeSelectedId={handleSingleSelectionChange}
            selectedIds={selectedIds}
            onChangeSelectedIds={handleMultipleSelectionChange}
            onClickItem={handleClickItem}
          >
            <SearchableList.SearchInput autoFocus={false} />
            <ListComponent
              shouldFilter={shouldFilter}
              className="!min-h-[200px]"
            >
              {children}
            </ListComponent>
          </SearchableList>
        </Drawer>
      </OpenContext.Provider>
    )
  }

  return (
    <OpenContext.Provider value={isOpen}>
      {triggerElement}
      <Popover
        placement={placement}
        className="tt-popover rounded-[theme(spacing[2.5])] flex flex-col overflow-hidden p-0"
        triggerRef={triggerRef}
        isOpen={isOpen}
        onOpenChange={handleOnOpenChange}
      >
        {/* @ts-ignore - TODO: fix this */}
        <SearchableList
          search={search}
          onChangeSearch={setSearch}
          selectionMode={selectionMode}
          selectedId={selectedId}
          onChangeSelectedId={handleSingleSelectionChange}
          selectedIds={selectedIds}
          onChangeSelectedIds={handleMultipleSelectionChange}
          onClickItem={handleClickItem}
        >
          <SearchableList.SearchInput
            innerRef={incomingSearchRef}
            placeholder={searchPlaceholder}
          />
          <ListComponent shouldFilter={shouldFilter}>{children}</ListComponent>
        </SearchableList>
      </Popover>
    </OpenContext.Provider>
  )
}

Combobox.Item = SearchableList.Item
Combobox.Divider = SearchableList.Divider
Combobox.GroupHeader = SearchableList.GroupHeader
Combobox.SearchInput = SearchInput
Combobox.SelectionIndicator = SearchableList.SelectionIndicator
Combobox.DropdownIcon = DropdownIcon
Combobox.EmptyState = SearchableList.EmptyState
